import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { CustomerSupportService } from "@obg/core.customer-support";
import { DialogService } from "@obg/mat.ui.core";
import {
	AbstractTranslatableAlertService,
	AuthSelector,
	CORE_ROUTE,
	CoreState,
	DEPOSIT_LIMIT_MESSAGE_CONTEXT,
	DepositLimit,
	DepositLimitAction,
	DepositLimitConfigService,
	DepositLimitSelector,
	DepositLimitType,
	DepositLimitUnconfirmedParams,
	DialogStatusSelector,
	JurisdictionPrefixService,
	Message,
	MessageService,
	MessageType,
} from "@obg/ngx.core";
import { TranslationService } from "@obg/ngx.core.content";
import { CurrencyService } from "@obg/ngx.global";
import { RouteAction } from "@obg/ngx.router";
import { MULTI_DEPOSIT_LIMIT_MESSAGE_DIALOG } from "@obg/ngx.ui.common/src/responsible-gaming/deposit-limit/multi-deposit-limit-message/multi-deposit-limit-message.dialog";
import { combineLatest, debounce, merge, Observable, of, timer } from "rxjs";
import { filter, map, pairwise, switchMap, take, tap } from "rxjs/operators";

const DEPOSIT_LIMIT_RECONFIRMATION_ALERT_ID = "DEPOSIT_LIMIT_RECONFIRMATION_ALERT_ID";
const DEPOSIT_LIMIT_RECONFIRMATION_ACCEPT_SUCCESS_ALERT_ID = "DEPOSIT_LIMIT_RECONFIRMATION_ACCEPT_SUCCESS_ALERT_ID";
const DEPOSIT_LIMIT_RECONFIRMATION_DECLINE_SUCCESS_ALERT_ID = "DEPOSIT_LIMIT_RECONFIRMATION_DECLINE_SUCCESS_ALERT_ID";
const DEPOSIT_LIMIT_RECONFIRMATION_ERROR_ALERT_ID = "DEPOSIT_LIMIT_RECONFIRMATION_ERROR_ALERT_ID";

@Injectable({
	providedIn: "root",
})
export class DepositLimitReconfirmationService {
	isMultiDepositLimitReconfirmationEnabled: boolean | undefined;

	constructor(
		private store: Store<CoreState>,
		private alertService: AbstractTranslatableAlertService,
		private currencyService: CurrencyService,
		private messageService: MessageService,
		private translationService: TranslationService,
		private jurisdictionPrefixService: JurisdictionPrefixService,
		private depositLimitAction: DepositLimitAction,
		private depositLimitSelector: DepositLimitSelector,
		private depositLimitConfigService: DepositLimitConfigService,
		private customerSupportService: CustomerSupportService,
		private dialogService: DialogService,
		private dialogStatusSelector: DialogStatusSelector
	) {}

	initialize$(): Observable<void> {
		this.isMultiDepositLimitReconfirmationEnabled =
			this.depositLimitConfigService.getIsMultiDepositLimitReconfirmationEnabled();

		const showDepositLimitReconfirmation$ = combineLatest([
			this.store.select(this.depositLimitSelector.getUnconfirmed(DepositLimitType.ResponsibleGaming)),
			this.store.select(this.depositLimitSelector.get(DepositLimitType.ResponsibleGaming)),
		]).pipe(
			filter(([unconfirmedLimit, currentLimit]) => !!unconfirmedLimit && !!currentLimit),
			tap(([unconfirmedLimit, currentLimit]) =>
				this.showDepositLimitReconfirmation(unconfirmedLimit, currentLimit!, !!unconfirmedLimit.amount)
			)
		);

		const showMultiDepositLimitReconfirmation$ = this.afterLogin$.pipe(
			switchMap(() =>
				this.store.pipe(select(this.depositLimitSelector.getAllUnconfirmed())).pipe(
					filter(
						(depositLimitsUnconfirmed) =>
							!!depositLimitsUnconfirmed && Boolean(this.isMultiDepositLimitReconfirmationEnabled)
					),
					take(1),
					switchMap((depositLimitsUnconfirmed) =>
						this.store.pipe(select(this.dialogStatusSelector.hasOpenDialogs())).pipe(
							debounce((hasOpenDialogs) => (!hasOpenDialogs ? timer(500) : of())),
							filter((hasOpenDialogs) => !hasOpenDialogs),
							take(1),
							map(() => depositLimitsUnconfirmed)
						)
					),
					map((depositLimitsUnconfirmed) => {
						return this.dialogService.openComponent(MULTI_DEPOSIT_LIMIT_MESSAGE_DIALOG, {
							width: "490px",
							data: {
								newDepositLimits: depositLimitsUnconfirmed,
								displayInfoText: false,
								hideFooter: true,
								hideClose: true,
								isPostLogin: true,
								disableClose: true,
							},
						});
					})
				)
			)
		);

		return merge(
			showDepositLimitReconfirmation$,
			showMultiDepositLimitReconfirmation$,
			this.handleMessages$()
		).pipe(map(() => undefined));
	}

	private showDepositLimitReconfirmation(
		unconfirmedLimit: DepositLimit,
		currentLimit: DepositLimit,
		isRaising = false
	) {
		const messageKey = isRaising
			? "deposit-limit.reconfirmation.raise-popup.message"
			: "deposit-limit.reconfirmation.remove-popup.message";

		this.jurisdictionPrefixService
			.addJurisdictionPrefix$(messageKey)
			.pipe(
				take(1),
				tap((message) => {
					this.alertService.open(
						{
							id: DEPOSIT_LIMIT_RECONFIRMATION_ALERT_ID,
							title: "deposit-limit.reconfirmation.popup.title",
							message,
							buttons: [
								{
									text: "general.reject",
									handler: () => this.settleUnconfirmedLimit(unconfirmedLimit, false),
								},
								{
									text: "general.accept",
									handler: () => this.settleUnconfirmedLimit(unconfirmedLimit, true),
								},
							],
							hideClose: true,
							dialogConfig: {
								disableClose: true,
							},
							mustBeInteracted: true,
						},
						[],
						{
							currentAmount: this.currencyService.format(currentLimit.amount),
							currentPeriod: this.translationService.getByKey(
								`deposit-limit.limit.option.${currentLimit.period}`.toLowerCase()
							),
							queuedAmount: this.currencyService.format(unconfirmedLimit.amount),
							queuedPeriod: this.translationService.getByKey(
								`deposit-limit.limit.option.${unconfirmedLimit.period}`.toLowerCase()
							),
						}
					);
				})
			)
			.subscribe(); // eslint-disable-line rxjs/no-ignored-subscription
	}

	private settleUnconfirmedLimit(unconfirmedLimit: DepositLimit, hasAccepted: boolean) {
		const params: DepositLimitUnconfirmedParams = {
			depositLimitId: unconfirmedLimit.depositLimitId,
			depositLimitType: unconfirmedLimit.depositLimitType,
		};

		this.store.dispatch(
			hasAccepted
				? this.depositLimitAction.acceptUnconfirmed(params)
				: this.depositLimitAction.declineUnconfirmed(params)
		);
	}

	private handleMessages$(): Observable<Message[]> {
		const successMessage$ = this.messageService
			.consume$({
				context: [
					DEPOSIT_LIMIT_MESSAGE_CONTEXT.acceptUnconfirmed,
					DEPOSIT_LIMIT_MESSAGE_CONTEXT.declineUnconfirmed,
				],
				messageType: MessageType.Success,
			})
			.pipe(
				tap(([message]) => {
					const isAccept = message.context === DEPOSIT_LIMIT_MESSAGE_CONTEXT.acceptUnconfirmed;

					this.alertService.open(
						{
							id: isAccept
								? DEPOSIT_LIMIT_RECONFIRMATION_ACCEPT_SUCCESS_ALERT_ID
								: DEPOSIT_LIMIT_RECONFIRMATION_DECLINE_SUCCESS_ALERT_ID,
							title: "deposit-limit.reconfirmation.popup.title",
							message: isAccept
								? "deposit-limit.reconfirmation.accept-success.message"
								: "deposit-limit.reconfirmation.decline-success.message",
							buttons: [
								{
									text: "general.close",
								},
							],
							hideClose: true,
							dialogConfig: {
								disableClose: true,
							},
							mustBeInteracted: true,
						},
						[]
					);
				})
			);

		const errorMessage$ = this.messageService
			.consume$({
				context: [
					DEPOSIT_LIMIT_MESSAGE_CONTEXT.acceptUnconfirmed,
					DEPOSIT_LIMIT_MESSAGE_CONTEXT.declineUnconfirmed,
				],
				messageType: MessageType.Error,
			})
			.pipe(
				tap(() => {
					this.alertService.open(
						{
							id: DEPOSIT_LIMIT_RECONFIRMATION_ERROR_ALERT_ID,
							title: "deposit-limit.reconfirmation.popup.title",
							message: "deposit-limit.reconfirmation.fail.message",
							buttons: [
								{
									text: "general.contact",
									handler: () => this.customerSupportService.openSupportPageInNewTab(),
								},
								{
									text: "deposit-limit.reconfirmation.fail.try-again",
									handler: () =>
										this.store.dispatch(
											RouteAction.navigate(CORE_ROUTE.responsibleGaming.depositLimit)
										),
								},
							],
							hideClose: true,
							dialogConfig: {
								disableClose: true,
							},
							mustBeInteracted: true,
						},
						[]
					);
				})
			);

		return merge(successMessage$, errorMessage$);
	}

	private afterLogin$ = this.store.select(AuthSelector.isAuthenticated).pipe(
		pairwise(),
		filter(([prev, next]) => !prev && next)
	);
}
